import React from 'react';
import { HeaderModeType } from '../types/Header';
import { BurgerMenuConfig } from '../types/BurgerMenu';
import ErrorBoundary from '../components/ErrorBoundary';
import Header, { HeaderProps } from '../components/Header';
import Account from './tray/Account';
import CartIcon from './tray/CartIcon';
import ReferAFriend from './tray/ReferAFriend';
import Share from './tray/Share';
import { SortButton } from './tray/SortButton';
import { HeaderStandaloneProps } from './types/headerStandalone';
import DynamicTitle from './DynamicTitle';
import { HeaderVariant } from './HeaderStandalone';
import InfoBlockItem from './InfoBlockItem';
import InfoBlockItemHelpcenter from './InfoBlockItemHelpcenter';
import { handleBackButtonClick } from './helpers/handleBackButton';

export const getTabsConfig = ({
    variant,
    t,
    indexPageHref,
}: Pick<HeaderStandaloneProps, 'variant' | 't' | 'indexPageHref'>): HeaderProps['tabsConfig'] => [
    {
        linkText: t('OwwHeader_Tab Shop'),
        linkUrl: `/?utm_source=club&utm_medium=referral&utm_campaign=club-shop-tab&utm_content=${
            variant === 'top_only' ? 'burger-menu' : 'header'
        }`,
        isSelected: false,
        trackingAction: variant === 'top_only' ? 'Club-Shop tab click – Menu' : 'Club-Shop tab click',
        noOverlay: true,
        testId: 'oww-header-shop-tab',
    },
    {
        linkText: t('OwwHeader_Tab Club'),
        linkUrl: `${indexPageHref}/`,
        isSelected: true,
        trackingAction: 'Club tab click',
        testId: 'oww-header-club-tab',
    },
];

const createHeaderProps = ({
    variant = 'default',
    cart,
    t,
    title,
    loginState,
    trackingProvider,
    infoBlockFreeReturnsUrl,
    infoBlockDaysToReturnUrl,
    indexPageHref,
    isOneCart,
    burgerMenuOnClick,
    headerMode,
    clubStaticHost,
    headerIcon,
    miniHeaderConfig,
}: HeaderStandaloneProps): React.ComponentProps<typeof Header> => {
    const burgerMenuConfig: BurgerMenuConfig = {
        onClick: () => {
            burgerMenuOnClick(true);
            trackingProvider.ga.trackNonInteractiveGenericEvent('Header', { action: 'Menu Show' });
        },
        dataTestId: 'oww-header-burger-menu',
    };
    const headerProps = {
        logoHref: `${indexPageHref}/`,
        tabsConfig: getTabsConfig({ variant, t, indexPageHref }),
        burgerMenuConfig,
        iconTrayComponents: [
            <ReferAFriend key="refer-a-friend" displayOnMobile={variant !== 'cdp'} />,
            <ErrorBoundary fallbackComponent={null} key="account">
                <Account
                    key="account"
                    isLoggedIn={loginState?.isLoggedIn ?? false}
                    isPartiallyLoggedIn={loginState?.isPartiallyLoggedIn ?? false}
                />
            </ErrorBoundary>,
            <ErrorBoundary fallbackComponent={null} key="cart">
                <CartIcon
                    key="club-cart-icon"
                    formattedQuantity={cart.formattedQuantity}
                    secondsLeft={cart.secondsLeft}
                    remindAt={cart.remindAt}
                    showMinicartAt={cart.showMinicartAt}
                    products={cart.products}
                    totalPrice={cart.totalPrice}
                    isOneCart={isOneCart}
                    loginState={loginState}
                    clubStaticHost={clubStaticHost}
                    headerIcon={headerIcon}
                    variant={variant}
                />
            </ErrorBoundary>,
        ],
        middleAreaComponent: <DynamicTitle>{title}</DynamicTitle>,
        infoBlockComponents: [
            <InfoBlockItem
                key="free_return"
                text={t('OwwHeader_Free Returns New')}
                trackingLabel="Free returns"
                url={infoBlockFreeReturnsUrl}
            />,
            <InfoBlockItem
                key="days_to_return"
                text={t('OwwHeader_Days for return header')}
                trackingLabel="Return period"
                url={infoBlockDaysToReturnUrl}
            />,
            <InfoBlockItemHelpcenter key="help_center_link" />,
        ],
        headerMode: headerMode || HeaderModeType.DEFAULT,
        miniHeaderConfig: {
            handleBackButtonClick,
            iconTrayComponents: [
                <CartIcon
                    key="club-cart-icon"
                    formattedQuantity={cart.formattedQuantity}
                    secondsLeft={cart.secondsLeft}
                    remindAt={cart.remindAt}
                    showMinicartAt={cart.showMinicartAt}
                    products={cart.products}
                    totalPrice={cart.totalPrice}
                    isOneCart={isOneCart}
                    clubStaticHost={clubStaticHost}
                    loginState={loginState}
                    headerIcon={headerIcon}
                    variant={variant}
                />,
            ],
            isLogoVisible: false,
            withShadow: variant !== 'cart',
            backButtonTestId: 'oww-header-mini-back',
            ...(miniHeaderConfig || {}),
        },
        shouldShowAppBanner: false,
    };

    const variantHandlers: Record<HeaderVariant, () => void> = {
        cart: () => {
            headerProps.headerMode = HeaderModeType.MINI;
            headerProps.miniHeaderConfig.isLogoVisible = true;
        },
        cdp: () => {
            headerProps.iconTrayComponents = [<SortButton key="sortButton" />, ...headerProps.iconTrayComponents];
        },
        pdp: () => {
            headerProps.headerMode = HeaderModeType.MINI;
            headerProps.miniHeaderConfig.iconTrayComponents = [
                <ErrorBoundary key="share-button" fallbackComponent={null}>
                    <Share />
                </ErrorBoundary>,
                ...(headerProps.miniHeaderConfig.iconTrayComponents || []),
            ];
        },
        // eslint-disable-next-line camelcase
        top_only: () => {
            headerProps.headerMode = HeaderModeType.FIRST_ROW;
        },
        default: () => undefined,

        'default-smart-banner': () => {
            variantHandlers.default();
            headerProps.shouldShowAppBanner = true;
        },
        'pdp-smart-banner': () => {
            variantHandlers.pdp();
            headerProps.shouldShowAppBanner = true;
        },
        'cdp-smart-banner': () => {
            variantHandlers.cdp();
            headerProps.shouldShowAppBanner = true;
        },
    };

    variantHandlers[variant]();
    return headerProps;
};

export default createHeaderProps;
